/**
 * @module composables/useDashboardPolling
 * @description 대시보드 자동 갱신(폴링) 관리 composable.
 * 1분(60초) 주기로 대시보드 데이터를 자동 갱신하며,
 * 브라우저 탭 비활성화 시 자동으로 폴링을 중지하고
 * 탭 활성화 시 재개합니다.
 */
import { ref, onMounted, onUnmounted } from 'vue'

// ── 상수 (Constants) ──

/** 폴링 주기 (밀리초) - 5분 */
const INTERVAL_MS = 300000

/**
 * 대시보드 자동 갱신(폴링)을 관리하는 composable입니다.
 * useDashboardData가 반환하는 객체를 인자로 받아
 * fetchAll을 주기적으로 호출합니다.
 *
 * 기능:
 * - 1분 주기 자동 갱신
 * - 폴링 토글 (시작/중지)
 * - 브라우저 탭 비활성화 시 자동 중지, 활성화 시 자동 재개
 * - 컴포넌트 언마운트 시 자동 정리
 *
 * @param {{
 *   fetchAll: () => Promise<void>
 * }} dashboardData - useDashboardData()가 반환하는 객체
 *
 * @returns {{
 *   isPolling: import('vue').Ref<boolean>,
 *   togglePolling: () => void
 * }}
 */
export function useDashboardPolling(dashboardData) {
  // ── 상태 (State) ──

  /** 폴링 활성화 상태 */
  const isPolling = ref(true)

  /** setInterval 타이머 ID */
  let timerId = null

  // ── 메서드 (Methods) ──

  /**
   * 폴링을 시작합니다.
   * 이미 타이머가 동작 중이면 중복 생성하지 않습니다.
   *
   * @returns {void}
   */
  function startPolling() {
    if (timerId !== null) {
      return
    }
    timerId = setInterval(() => {
      dashboardData.fetchAll()
    }, INTERVAL_MS)
  }

  /**
   * 폴링을 중지합니다.
   * 타이머를 해제하고 ID를 초기화합니다.
   *
   * @returns {void}
   */
  function stopPolling() {
    if (timerId !== null) {
      clearInterval(timerId)
      timerId = null
    }
  }

  /**
   * 폴링 상태를 토글합니다.
   * 활성화 시 폴링을 시작하고, 비활성화 시 폴링을 중지합니다.
   *
   * @returns {void}
   */
  function togglePolling() {
    isPolling.value = !isPolling.value
    if (isPolling.value) {
      startPolling()
    } else {
      stopPolling()
    }
  }

  /**
   * 브라우저 탭 가시성 변경 이벤트 핸들러입니다.
   * 탭이 숨겨지면 폴링을 중지하고, 탭이 다시 보이면
   * isPolling이 true인 경우에만 폴링을 재개합니다.
   *
   * @returns {void}
   */
  const handleVisibilityChange = () => {
    if (document.hidden) {
      stopPolling()
    } else if (isPolling.value) {
      startPolling()
    }
  }

  // ── 라이프사이클 훅 (Lifecycle Hooks) ──

  onMounted(() => {
    startPolling()
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  onUnmounted(() => {
    stopPolling()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return {
    isPolling,
    togglePolling
  }
}
